import {
  Alert,
  Box,
  Button,
  Divider,
  Icons,
  iconSize,
  Link,
  Stack,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type { FormSummary } from '@munifocus/contracts-gov';
import { formatDateTime } from '../lib/relative-time';
import { UnavailableDownload } from './UnavailableDownload';

/**
 * The document-assembly surface shared by the escalation steps that issue a
 * statutory form (Step 5 Form 4, Step 8 Form 5). It generates the PDF on demand
 * and lists every document produced for the file with a (presigned) download
 * link. The pre-populated preview card stays with each step; this owns the
 * "produce the signed PDF" action and the resulting document trail.
 */
export function GeneratedDocuments({
  forms,
  onGenerate,
  generating,
  isError,
  generateLabel,
  disabled = false,
}: {
  forms: FormSummary[];
  onGenerate: () => void;
  generating: boolean;
  isError: boolean;
  generateLabel: string;
  /** Blocks generation while the calling step's own inputs are unusable. */
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();

  return (
    <Stack spacing={1.5}>
      {isError && <Alert severity="error">{t('gov.forms.generateError')}</Alert>}

      <Box>
        <Button
          variant="outlined"
          startIcon={<Icons.FileText />}
          onClick={onGenerate}
          disabled={generating || disabled}
        >
          {generating ? t('gov.forms.generating') : generateLabel}
        </Button>
      </Box>

      {forms.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant="overline" color="text.secondary">
            {t('gov.forms.generatedDocuments')}
          </Typography>
          <Stack spacing={0.5} divider={<Divider flexItem />}>
            {forms.map((form) => (
              <Stack
                key={form.id}
                direction="row"
                sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.5 }}
              >
                {form.url ? (
                  <Link
                    href={form.url}
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <Icons.Download size={iconSize.md} />
                    {t(`gov.forms.kind.${form.kind}`)}
                  </Link>
                ) : (
                  <UnavailableDownload iconSize={16} gap={0.75}>
                    {t(`gov.forms.kind.${form.kind}`)}
                  </UnavailableDownload>
                )}
                <Typography variant="caption" color="text.secondary">
                  {form.url
                    ? formatDateTime(form.generatedAt, i18n.language)
                    : t('gov.forms.notGenerated')}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
